import React, { useState, useEffect, useRef, useCallback }            from 'react';
import { Snackbar, Alert, AlertTitle, Box, ThemeProvider, Collapse } from '@mui/material';
import muiThemeNew                                                   from 'config/muiThemeNew';
import { TransitionGroup }                                           from 'react-transition-group';

// Create a custom event for notifications
const ADD_NOTIFICATION_EVENT = 'add-notification';
const CLEAR_NOTIFICATIONS_EVENT = 'clear-notifications';

// Function to trigger notifications from anywhere
export const notify = ({message, options}: {message: string, options?: NotificationEventOptions}) => {
  const event = new CustomEvent<NotificationEvent>(ADD_NOTIFICATION_EVENT, {
    detail: { message, options }
  });
  window.dispatchEvent(event);
};

export const clearNotifications = () => {
  const event = new CustomEvent<NotificationEvent>(CLEAR_NOTIFICATIONS_EVENT);
  window.dispatchEvent(event);
};

// Default auto-dismiss for transient toasts (success/info/warning). Errors
// stay sticky by default so the user actually reads them; callers can still
// pass an explicit `duration` to override either way (caller-supplied values
// win because they come after the default in the spread).
const DEFAULT_TRANSIENT_DURATION_MS = 4000;

// Helper function for each notification type
notify.success = (message: string, options?: NotificationEventOptions) =>
  notify({message, options: {duration: DEFAULT_TRANSIENT_DURATION_MS, ...options, type: 'success'}});
notify.error = (message: string, options?: NotificationEventOptions) =>
  notify({message, options: {...options, type: 'error'}});
notify.info = (message: string, options?: NotificationEventOptions) =>
  notify({message, options: {duration: DEFAULT_TRANSIENT_DURATION_MS, ...options, type: 'info'}});
notify.warning = (message: string, options?: NotificationEventOptions) =>
  notify({message, options: {duration: DEFAULT_TRANSIENT_DURATION_MS, ...options, type: 'warning'}});

export default function AppNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // Refs not state: the listener is registered once with [] deps so any
  // state captured in its closure would stick at its initial value
  // (every toast getting id=0, etc.). Refs sidestep the stale-closure trap.
  const nextIdRef = useRef(0);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const handleClose = useCallback((id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  useEffect(() => {
    const handleAdd = (event: CustomEvent<NotificationEvent>) => {
      const id = nextIdRef.current++;
      const newNotification: Notification = {
        message: event.detail.message,
        options: event.detail.options,
        id
      };
      setNotifications(prev => [...prev, newNotification].slice(-5));

      if (newNotification.options?.duration) {
        const timeout = setTimeout(() => {
          timeoutsRef.current.delete(timeout);
          handleClose(id);
        }, newNotification.options.duration);
        timeoutsRef.current.add(timeout);
      }
    };

    const handleClear = () => setNotifications([]);

    window.addEventListener(ADD_NOTIFICATION_EVENT, handleAdd as EventListener);
    window.addEventListener(CLEAR_NOTIFICATIONS_EVENT, handleClear as EventListener);
    return () => {
      window.removeEventListener(ADD_NOTIFICATION_EVENT, handleAdd as EventListener);
      window.removeEventListener(CLEAR_NOTIFICATIONS_EVENT, handleClear as EventListener);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current.clear();
    };
  }, [handleClose]);

  return (
    // We have to import the theme here because the Snackbar component doesn't support the theme prop
    <ThemeProvider theme={muiThemeNew}>
      <Snackbar
        open={notifications.length > 0}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
          <TransitionGroup >
            {notifications.map(notification => (
              <Collapse key={notification.id} >
                <Alert
                  key={notification.id}
                  onClose={() => handleClose(notification.id)}
                  severity={notification.options?.type}
                  variant="standard"
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    mb: 1,
                    mt: 1.5
                  }}
                  >
                  {notification.options?.title && <AlertTitle>{notification.options.title}</AlertTitle>}
                  {notification.message}
                </Alert>
              </Collapse>
            ))}
          </TransitionGroup>
        </Box>
      </Snackbar>
    </ThemeProvider>
  );
}

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationEvent {
  message: string;
  options?: NotificationEventOptions;
}

interface NotificationEventOptions {
  title?: string;
  type?: NotificationType;
  duration?: number;
}

interface Notification extends NotificationEvent {
  id: number;
}
